import { useMemo, useTransition } from 'react';
import {
    goBack,
    goToStep,
    setCurrentStepValidationResult,
    setIsSubmitting,
    setIsValidating,
    updateStepData,
} from '../store/wizardSlice';
import { useWizardContext } from './useWizardContext';
import { RootState, useAppDispatch } from 'store';
import { useSelector } from 'react-redux';
import { ValidationResult } from 'types/validation';
import { isGeneralException, isValidationProblemDetailsResponse, validationResultUtils } from 'utils/validation';
import { AxiosError, HttpStatusCode } from 'axios';

export function useWizardNavigation<TFormData extends Record<string, any>>() {
    const dispatch = useAppDispatch();
    const { config } = useWizardContext();
    const [, startTransition] = useTransition();

    const { currentStepId, history, formData } = useSelector((state: RootState) => state.wizard);

    const currentStep = useMemo(() => {
        const step = config.steps[currentStepId];
        if (!step) {
            throw new Error(`Current step "${currentStepId}" not found in the wizard config`);
        }
        return step;
    }, [config, currentStepId]);

    const isLastStep = useMemo(() => !!currentStep && !currentStep.next, [currentStep]);

    const isFirstStep = useMemo(() => !!currentStep && history.length <= 1, [currentStep, history]);

    const canGoBack = useMemo(() => history.length > 1, [history]);

    const handleBack = () => {
        dispatch(setIsSubmitting(true));

        startTransition(() => {
            dispatch(goBack());
        });

        setTimeout(() => {
            dispatch(setIsSubmitting(false));
        }, 200);
    };

    const validateAndSetFormData = async (
        values: Partial<TFormData>
    ): Promise<ValidationResult<keyof TFormData> | null> => {
        try {
            dispatch(setIsValidating(true));
            const validate = currentStep?.validate;
            if (validate) {
                if (typeof validate !== 'function') {
                    throw new Error('Invalid step validation function. Expected a function, got: ' + typeof validate);
                }
                const validationResult = await validate(values, formData);

                if (validationResult && !validationResultUtils.isEmpty(validationResult)) {
                    dispatch(setCurrentStepValidationResult({ stepId: currentStepId, validation: validationResult }));
                    if (validationResultUtils.hasErrors(validationResult)) {
                        return validationResult;
                    }
                } else {
                    dispatch(
                        setCurrentStepValidationResult({
                            stepId: currentStepId,
                            validation: validationResultUtils.createEmpty(),
                        })
                    );
                }
            }

            // update wizard state
            dispatch(updateStepData({ stepId: currentStepId, data: values }));
            return null;
        } catch (error) {
            // If response is a bad request we can try to return form errors
            if (error instanceof AxiosError && error.response?.status === HttpStatusCode.BadRequest) {
                const data = error.response?.data;
                if (isValidationProblemDetailsResponse(data)) {
                    const res = validationResultUtils.createEmpty<keyof TFormData>();
                    for (const err of Object.entries(data.errors || {})) {
                        const [key, messages] = err;
                        res.issues[0].fields.push({
                            name: key as keyof TFormData,
                            value: null,
                            validationMessages: messages.map((msg) => ({
                                severity: 'Error',
                                message: msg,
                            })),
                        });
                    }
                    return res;
                }

                if (isGeneralException(data)) {
                    const res = validationResultUtils.createEmpty<keyof TFormData>();
                    res.globalIssues.push({
                        message: data.error,
                        severity: 'Error',
                    });
                    return res;
                }
            }
            throw error;
        } finally {
            dispatch(setIsValidating(false));
        }
    };

    const handleNext = async (values?: Partial<TFormData>): Promise<ValidationResult<keyof TFormData> | void> => {
        try {
            if (values) {
                const validationResult = await validateAndSetFormData(values);
                if (validationResult && !validationResultUtils.isEmpty(validationResult)) {
                    return validationResult;
                }
            }

            dispatch(setIsSubmitting(true));
            // navigate to next step
            const next = currentStep?.next;

            let nextStepId: string | null = null;
            if (typeof next === 'string') {
                nextStepId = next;
            } else if (next && typeof next === 'function') {
                nextStepId = next(values, formData) || null;
            }

            startTransition(() => {
                if (!nextStepId) {
                    dispatch(setIsSubmitting(false));
                    throw new Error('No next step found');
                }
                dispatch(goToStep({ stepId: nextStepId }));
            });

            setTimeout(() => {
                dispatch(setIsSubmitting(false));
            }, 200);
        } catch (error) {
            dispatch(setIsSubmitting(false));
            throw error;
        }
    };

    return {
        isLastStep,
        isFirstStep,
        canGoBack,
        currentStep,
        validateAndSetFormData,
        handleBack,
        handleNext,
    };
}
